import { useMemo, useState, useEffect } from "react";
import { useFiscalStore } from "@/store/fiscal-store";
import { fmtBRL, fmtNum, fmtDate, maskCnpjCpf, maskName } from "@/lib/format";
import type { NotaSimplificada, NotaStatus } from "@/lib/fiscal-types";
import { Search, Database, Download, Layers, Activity, History, ArrowLeft, ArrowRight, ArrowDownUp, ShieldCheck, Users } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotaDetailModal } from "@/components/NotaDetailModal";
import { StatusBadge } from "@/components/StatusBadge";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { exportNotesToPDF } from "@/lib/export-pdf";

const PAGE = 50;

export default function FornecedoresPage() {
  const { filteredNotas, dashboard, filtroStatus, setFiltroStatus, privacyMode } = useFiscalStore();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [sortKey, setSortKey] = useState<"numero" | "emissao" | "valorOficial">("emissao");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  useEffect(() => { document.title = "Fornecedores (Entrada) — DataGrid XML"; }, []);
  useEffect(() => { setPage(0); }, [filtroStatus, q]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const arr = filteredNotas.filter((n) => {
      // Intelligent filter for ENTRADA
      let op = n.tipoOperacao;
      const topCnpj = dashboard?.cnpjEmpresa?.replace(/\D/g, "");
      
      if (op === "INDEFINIDA" && topCnpj) {
        if (n.cnpjDest.replace(/\D/g, "") === topCnpj) op = "ENTRADA";
        else if (n.cnpjEmit.replace(/\D/g, "") === topCnpj) op = "SAIDA";
      }

      if (op !== "ENTRADA") return false;
      
      if (filtroStatus && n.notaStatus !== filtroStatus) return false;
      if (ql && !(
        n.chave.toLowerCase().includes(ql) ||
        n.numero.toLowerCase().includes(ql) ||
        n.emitente.toLowerCase().includes(ql) ||
        n.cnpjEmit.toLowerCase().includes(ql)
      )) return false;
      return true;
    });

    return arr.sort((a, b) => {
      const factor = sortDir === "asc" ? 1 : -1;
      if (sortKey === "numero") return (parseInt(a.numero) - parseInt(b.numero)) * factor;
      if (sortKey === "emissao") return ((a.emissao || "").localeCompare(b.emissao || "")) * factor;
      if (sortKey === "valorOficial") return (a.valorOficial - b.valorOficial) * factor;
      return 0;
    });
  }, [filteredNotas, q, filtroStatus, sortKey, sortDir]);

  const slice = filtered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };

  const totalValueFiltered = filtered.reduce((acc, n) => acc + n.valorOficial, 0);

  return (
    <div className="p-4 lg:p-6 max-w-[1600px] mx-auto space-y-6 animate-in fade-in duration-1000">
      <header className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 border-l-4 border-emerald-500 pl-8 py-4 mb-10 bg-white/[0.02] glass-technical border-y-0 border-r-0 rounded-r-3xl">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="size-3 text-emerald-500 animate-pulse" />
            <span className="text-[10px] font-black uppercase tracking-[0.4em] text-emerald-500">Inbound Supply Chain</span>
          </div>
          <h1 className="text-5xl font-black tracking-tighter leading-none mb-2">
            Fornecedores <span className="text-emerald-500">(Entrada)</span>
          </h1>
          <p className="text-[12px] text-muted-foreground font-black uppercase tracking-[0.1em] mt-2 flex items-center gap-6 opacity-70">
             <span className="flex items-center gap-2 uppercase tabular-nums"><Layers className="size-3.5 text-emerald-500" /> {fmtNum(filtered.length)} DOCUMENTOS DE ENTRADA</span>
             <span className="flex items-center gap-2 uppercase tabular-nums"><Activity className="size-3.5 text-emerald-500" /> Supply Network Active</span>
          </p>
        </div>
        
        <div className="flex items-center gap-4">
           <div className="glass-technical px-6 py-3 border-emerald-500/20 flex flex-col justify-center">
              <span className="text-[8px] font-black text-muted-foreground uppercase tracking-widest leading-none mb-1">Total Entradas</span>
              <span className="text-xl font-black text-emerald-500 tabular-nums leading-none tracking-tighter">{fmtBRL(totalValueFiltered)}</span>
           </div>
           <Button 
             variant="outline" 
             className="h-12 rounded-2xl bg-white/5 border-foreground/5 hover:bg-emerald-500/5 hover:border-emerald-500/20 transition-all font-black text-[10px] uppercase tracking-widest px-6"
             onClick={() => exportNotesToPDF(
               filtered,
               {
                 title: "Relatório de Fornecedores (Entrada)",
                 filename: "fornecedores",
                 companyName: dashboard?.nomeEmpresa,
                 cnpj: dashboard?.cnpjEmpresa,
                 periodo: useFiscalStore.getState().filtroPeriodo
               },
               [
                 { header: "Emissão", dataKey: "emissao" },
                 { header: "Número", dataKey: "numero" },
                 { header: "Fornecedor", dataKey: "emitente" },
                 { header: "CNPJ", dataKey: "cnpj" },
                 { header: "Valor", dataKey: "valor" }
               ],
               (n) => ({
                 emissao: fmtDate(n.emissao),
                 numero: n.numero,
                 emitente: n.emitente,
                 cnpj: n.cnpjEmit,
                 valor: fmtBRL(n.valorOficial)
               })
             )}
           >
              <Download className="size-4 mr-2" /> Exportar Lista
           </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        {/* Concentration Analysis */}
        <div className="xl:col-span-8 glass-card p-8 border-emerald-500/5 bg-white/[0.01]">
           <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-sm font-black uppercase tracking-widest flex items-center gap-2">
                  <Activity className="size-4 text-emerald-500" /> Concentração de Compras
                </h3>
                <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest mt-1">Participação Financeira por Fornecedor (Top 5)</p>
              </div>
           </div>
           <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={useMemo(() => {
                  const map = new Map();
                  filtered.forEach(n => {
                    const name = n.emitente.slice(0, 20);
                    map.set(name, (map.get(name) || 0) + n.valorOficial);
                  });
                  return [...map.entries()]
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 5)
                    .map(([name, value]) => ({ name, value }));
                }, [filtered])} layout="vertical">
                  <XAxis type="number" hide />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 9, fontWeight: 900 }} width={120} />
                  <Tooltip contentStyle={{ backgroundColor: 'rgba(0,0,0,0.9)', border: 'none', borderRadius: '12px' }} />
                  <Bar dataKey="value" fill="#10B981" radius={[0, 4, 4, 0]} barSize={15} />
                </BarChart>
              </ResponsiveContainer>
           </div>
        </div>

        {/* Quick Insights */}
        <div className="xl:col-span-4 glass-card p-8 border-emerald-500/5 bg-emerald-500/[0.02] flex flex-col justify-between">
           <div>
              <h3 className="text-sm font-black uppercase tracking-widest mb-6">Supply Insights</h3>
              <div className="space-y-4">
                 <div className="p-4 rounded-2xl glass-technical border-emerald-500/10">
                    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground block mb-1">Maior Parceiro</span>
                    <span className="text-sm font-black truncate block">{filtered[0]?.emitente || "—"}</span>
                 </div>
                 <div className="p-4 rounded-2xl glass-technical border-emerald-500/10">
                    <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground block mb-1">Ticket Médio de Compra</span>
                    <span className="text-sm font-black block">{fmtBRL(totalValueFiltered / (filtered.length || 1))}</span>
                 </div>
              </div>
           </div>
           <div className="mt-6 pt-6 border-t border-foreground/5">
              <p className="text-[9px] font-black uppercase tracking-[0.2em] text-emerald-500">
                <div className="size-1.5 rounded-full bg-emerald-500 animate-ping inline-block mr-2" />
                Rede de suprimentos estável
              </p>
           </div>
        </div>
      </div>

      <div className="glass-card p-6 grid grid-cols-1 xl:grid-cols-12 gap-4 items-center border-emerald-500/5 bg-muted/5">
        <div className="xl:col-span-8 relative group">
          <Search className="size-4 absolute left-5 top-1/2 -translate-y-1/2 text-muted-foreground group-focus-within:text-emerald-500 transition-colors" />
          <input 
            placeholder="Buscar por Fornecedor, CNPJ ou NF..." 
            value={q} 
            onChange={(e) => { setQ(e.target.value); setPage(0); }} 
            className="input-premium w-full pl-14 h-14 rounded-2xl bg-white/10 border-foreground/5 text-xs font-bold uppercase tracking-tight"
          />
        </div>

        <div className="xl:col-span-4 flex items-center gap-3">
          <Select
            value={filtroStatus ?? "all"}
            onValueChange={(v) => setFiltroStatus(v === "all" ? null : (v as NotaStatus))}
          >
            <SelectTrigger className="w-full h-14 rounded-2xl glass-technical border-foreground/5 font-black text-[10px] uppercase tracking-widest bg-white/5">
              <SelectValue placeholder="Situação" />
            </SelectTrigger>
            <SelectContent className="glass-card">
              <SelectItem value="all">SITUAÇÃO: TODOS</SelectItem>
              <SelectItem value="AUTORIZADA">AUTORIZADAS</SelectItem>
              <SelectItem value="CANCELADA">CANCELADAS</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="glass-card overflow-hidden border-emerald-500/5">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-muted/10 text-muted-foreground border-b border-foreground/5">
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] border-r border-foreground/5">Fornecedor / Emitente</th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] cursor-pointer hover:text-emerald-500 transition-colors border-r border-foreground/5 group" onClick={() => toggleSort("numero")}>
                  <div className="flex items-center gap-2">Documento <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] cursor-pointer hover:text-emerald-500 transition-colors border-r border-foreground/5 group" onClick={() => toggleSort("emissao")}>
                  <div className="flex items-center gap-2">Data Entrada <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-right border-r border-foreground/5 group" onClick={() => toggleSort("valorOficial")}>
                  <div className="flex items-center justify-end gap-2">Valor Total <ArrowDownUp className="size-3 opacity-20 group-hover:opacity-100" /></div>
                </th>
                <th className="p-5 text-[9px] font-black uppercase tracking-[0.3em] text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-foreground/[0.03]">
              {slice.map((n, idx) => (
                <tr 
                  key={n.chave} 
                  className="hover:bg-emerald-500/[0.05] transition-all cursor-pointer group" 
                  onClick={() => setOpenIndex(page * PAGE + idx)}
                >
                  <td className="p-5 border-r border-foreground/[0.03]">
                    <div className="flex items-center gap-4">
                      <div className="size-10 rounded-2xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 flex items-center justify-center font-black text-[10px] shadow-lg group-hover:rotate-6 transition-all">
                        {n.modelo}
                      </div>
                      <div>
                        <div className="text-sm font-black truncate max-w-[400px] tracking-tight group-hover:text-emerald-500 transition-colors">
                          {privacyMode ? maskName(n.emitente) : n.emitente}
                        </div>
                        <div className="text-[10px] font-black text-muted-foreground/60 mt-0.5 tracking-tighter tabular-nums flex items-center gap-1.5">
                          <ShieldCheck className="size-3 text-emerald-500" /> {privacyMode ? maskCnpjCpf(n.cnpjEmit) : n.cnpjEmit}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03] font-black text-sm tabular-nums tracking-tighter">
                    <span className="opacity-40 font-bold mr-1">#</span>{n.numero}
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03] text-xs font-black tabular-nums text-muted-foreground/80">
                    {fmtDate(n.emissao)}
                  </td>
                  <td className="p-5 border-r border-foreground/[0.03] text-right font-black text-emerald-500 tabular-nums text-lg tracking-tighter">
                    {fmtBRL(n.valorOficial)}
                  </td>
                  <td className="p-5">
                    <div className="flex justify-center">
                      <StatusBadge status={n.notaStatus} size="sm" />
                    </div>
                  </td>
                </tr>
              ))}
              {slice.length === 0 && (
                <tr>
                  <td colSpan={5} className="p-32 text-center">
                    <div className="flex flex-col items-center justify-center gap-4 opacity-10 animate-pulse">
                      <Database className="size-16" />
                      <span className="text-sm font-black uppercase tracking-[0.4em]">Nenhum Fornecedor Identificado</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center justify-between p-6 bg-muted/10 border-t border-emerald-500/10">
          <div className="flex items-center gap-4">
            <div className="size-10 rounded-xl glass-technical border-emerald-500/20 flex items-center justify-center text-emerald-500">
               <History className="size-5" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-muted-foreground/60">
              Registros <span className="text-foreground">{(page * PAGE) + 1}—{Math.min((page + 1) * PAGE, filtered.length)}</span> de {fmtNum(filtered.length)}
            </span>
          </div>

          <div className="flex items-center gap-6 mt-4 sm:mt-0">
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-emerald-500/40 transition-all">
                <ArrowLeft className="size-5" />
              </button>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)} className="btn-glass size-12 flex items-center justify-center rounded-2xl disabled:opacity-10 hover:border-emerald-500/40 transition-all">
                <ArrowRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <NotaDetailModal
        notas={filtered}
        index={openIndex}
        onClose={() => setOpenIndex(null)}
        onChangeIndex={setOpenIndex}
      />
    </div>
  );
}
